import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, FileText, CheckCircle2, ShieldCheck, Layers, PlayCircle, Clock, Sparkles, ArrowRight, Zap } from 'lucide-react';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Counter } from './ui/Counter';
import { AgencyModal } from './AgencyModal';
import { InteractiveProfile } from './InteractiveProfile';
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.21, 0.47, 0.32, 0.98]
    }
  }
};
// Floating activity chip
const FloatChip = ({
  className = '',
  icon,
  label,
  delay = 0
}: {
  className?: string;
  icon: React.ReactNode;
  label: string;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  scale: 0.8,
  y: 10
}} animate={{
  opacity: 1,
  scale: 1,
  y: 0
}} transition={{
  delay,
  duration: 0.5,
  ease: 'backOut'
}} className={`absolute z-20 hidden lg:flex items-center gap-2 bg-white/90 backdrop-blur-md border border-gray-100 shadow-float rounded-full pl-2 pr-3.5 py-1.5 ${className}`} data-id="element-661" data-mp="384s5oprwen3l">
    <motion.div animate={{
    y: [0, -5, 0]
  }} transition={{
    duration: 3.5,
    repeat: Infinity,
    ease: 'easeInOut',
    delay
  }} className="flex items-center gap-2" data-id="element-662" data-mp="30ib0az366mmy">
      {icon}
      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap" data-id="element-663" data-mp="2qlep2llvzmep">
        {label}
      </span>
    </motion.div>
  </motion.div>;
export const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-664" data-mp="2hljas7x1bbag">
      <AgencyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-665" data-mp="2lmv0edfwrwin" />
      {/* Animated gradient mesh background */}
      <motion.div animate={{
      x: [0, 40, 0],
      y: [0, -30, 0]
    }} transition={{
      duration: 18,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[1100px] h-[560px] bg-zano-lavender/50 rounded-full blur-3xl -z-10" data-id="element-666" data-mp="nbl42pi3sock" />
      <motion.div animate={{
      x: [0, -40, 0],
      y: [0, 30, 0]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-32 right-[-5%] w-[560px] h-[560px] bg-zano-blush/60 rounded-full blur-3xl -z-10" data-id="element-667" data-mp="1b0duy7jvc60b" />
      <motion.div animate={{
      x: [0, 30, 0]
    }} transition={{
      duration: 22,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-52 left-[-5%] w-[440px] h-[440px] bg-sky-100/50 rounded-full blur-3xl -z-10" data-id="element-668" data-mp="3lsyrwwxreii0" />
      {/* Subtle grid */}
      <div className="absolute inset-0 -z-10 opacity-[0.4]" style={{
      backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.025) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
      maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 40%, transparent 75%)'
    }} data-id="element-669" data-mp="21a4t5ua36g5g" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-670" data-mp="2kxc5znnkzdhf">
        <motion.div variants={container} initial="hidden" animate="show" className="max-w-4xl mx-auto" data-id="element-671" data-mp="2p08rbm4i9np2">
          <motion.div variants={item} data-id="element-672" data-mp="2tx7lu5s2zwns">
            <Badge className="mb-6 bg-white/80 backdrop-blur text-zano-pink border border-zano-pink/30 shadow-sm" data-id="element-673" data-mp="un9a5okh8lfo">
              <Sparkles size={13} className="mr-1.5" data-id="element-674" data-mp="zmc2c8fcyx68" /> The Elite Creator
              Network
            </Badge>
          </motion.div>

          <motion.h1 variants={item} className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-zano-charcoal mb-6 leading-[1.08]" data-id="element-675" data-mp="4d76l403va0u">
            The discovery layer for{' '}
            <span className="relative inline-block" data-id="element-676" data-mp="9aqku3wszmss">
              <span className="relative z-10 text-zano-pink" data-id="element-677" data-mp="9b8ovmppj5b9">
                represented creators
              </span>
              <motion.span initial={{
              scaleX: 0
            }} animate={{
              scaleX: 1
            }} transition={{
              delay: 0.7,
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute bottom-1 left-0 right-0 h-3 bg-zano-pink/15 -z-0 origin-left rounded" data-id="element-678" data-mp="3hnxu6k85oks5" />
            </span>
          </motion.h1>

          <motion.p variants={item} className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-679" data-mp="2u0g0gj5bzcmj">
            Search live agency rosters and connect directly with the managers
            who represent the world's best creators.
          </motion.p>

          <motion.div variants={item} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-680" data-mp="ziemsov9z7kr">
            <a href="https://www.youtube.com/watch?v=U2sV81k41u4&pp=0gcJCUwLAYcqIYzv" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-681" data-mp="1s8vj0bxo73c4">
              <Button variant="secondary" className="w-full sm:w-auto text-base px-8 py-3.5" data-id="element-682" data-mp="1j7x9xf8dk5mo">
                <PlayCircle size={18} className="mr-1.5" data-id="element-683" data-mp="7tbi57zh7v7l" /> Watch Demo Video
              </Button>
            </a>
            <Link to="/pricing" className="w-full sm:w-auto" data-id="element-684" data-mp="bvesftaestra">
              <Button variant="primary" className="w-full sm:w-auto text-base px-8 py-3.5" data-id="element-685" data-mp="bsslnvvceia4">
                Start Free Trial <ArrowRight size={18} className="ml-1.5" data-id="element-686" data-mp="29jio01v0bhp1" />
              </Button>
            </Link>
          </motion.div>

          {/* Trust strip */}
          <motion.div variants={item} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3" data-id="element-687" data-mp="24peh1ilqr9ny">
            <div className="flex items-center -space-x-2" data-id="element-688" data-mp="1vsnld7b3pwtw">
              <div className="w-8 h-8 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm relative z-0" data-id="element-689" data-mp="1qrgedags91oa">
                <img src="https://cdn.magicpatterns.com/uploads/aQUTmU5LHPMMPv6uVqWKZY/Screenshot_2026-07-06_at_16.16.09.png" alt="Viral Nation logo" className="w-full h-full object-cover scale-[2.1]" data-id="element-690" data-mp="1lty0r6rtly9y" />
              </div>
              <div className="w-8 h-8 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm relative z-10 -ml-4" data-id="element-691" data-mp="3b8w5h28xhzgu">
                <img src="https://cdn.magicpatterns.com/uploads/iMEkKfrRdejvDfRHdG5tcE/uta.png" alt="UTA logo" className="w-full h-full object-contain" data-id="element-692" data-mp="3joldn9zzygx5" />
              </div>
              {[{
              name: 'Connect Management',
              src: 'https://cdn.magicpatterns.com/uploads/fKq6FRuDXeQ3mJ1bWbqkVi/connectmgmt.png'
            }, {
              name: 'Clicks Talent',
              src: 'https://cdn.magicpatterns.com/uploads/8tVZ1Wn6UGbeE2qh1vffiq/clicks.jpg'
            }, {
              name: 'Famfluence',
              src: 'https://cdn.magicpatterns.com/uploads/iq5ZMVZ2aiG8SYPyU7XxgR/famfluence_logo.png'
            }].map((p, i) => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm relative z-0" data-id="element-693" data-mp="tz9mcprahiwp" data-mpi={i}>
                  <img src={p.src} alt={`${p.name} logo`} className="w-full h-full object-contain" data-id="element-694" data-mp="1f17sx1nqohd9" />
                </div>)}
            </div>
            <p className="text-sm text-gray-500 font-medium" data-id="element-695" data-mp="1k2eweoiumue4">
              <span className="font-semibold text-zano-charcoal" data-id="element-696" data-mp="1k0u1gxsmh9fq">
                <Counter to={400} suffix="+" data-id="element-697" data-mp="1yq00olrgmznm" />
              </span>{' '}
              talent agencies ·{' '}
              <span className="font-semibold text-zano-charcoal" data-id="element-698" data-mp="1ts6w1x5t2dpg">
                <Counter to={6500} suffix="+" data-id="element-699" data-mp="27r6icy5uigig" />
              </span>{' '}
              represented creators
            </p>
          </motion.div>
        </motion.div>

        {/* Interactive Profile Explainer */}
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.35
      }} className="mt-24 relative mx-auto max-w-6xl text-left" data-id="element-700" data-mp="dezwn5fbshbw">
          <InteractiveProfile data-id="element-701" data-mp="2p3egqadrg7rn" />
        </motion.div>
      </div>
    </section>;
};